/**
 * Quote Builder Helper Utilities
 * Extracted from ChecklistManagementQuoteBuilder for reusability
 */

import type {
  ChecklistComponent,
  ChecklistResponseEntry,
  ChecklistResponseValue,
  ChecklistScalarValue,
} from "@/types/quote.types";

/**
 * Type guard to check if a value is a ChecklistResponseEntry
 */
export const isResponseEntry = (value: ChecklistResponseValue): value is ChecklistResponseEntry => {
  return typeof value === "object" && value !== null && Object.hasOwn(value, "value");
};

/**
 * Extract scalar value from a ChecklistResponseValue
 */
export const extractScalarValue = (
  value: ChecklistResponseValue
): ChecklistScalarValue | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  return isResponseEntry(value)
    ? (value.value as ChecklistScalarValue)
    : (value as ChecklistScalarValue);
};

/**
 * Extract quantity from response or quantities record
 */
export const extractQuantity = (
  checklistId: number,
  fieldName: string,
  value?: ChecklistResponseValue,
  quantities?: Record<number, Record<string, number>>
): number | undefined => {
  if (value && isResponseEntry(value) && typeof value.quantity === "number") {
    return value.quantity;
  }

  return quantities?.[checklistId]?.[fieldName];
};

/**
 * Build a complete response entry with quantity
 */
export const buildResponseEntry = (
  checklistId: number,
  fieldName: string,
  value: ChecklistResponseValue,
  quantities?: Record<number, Record<string, number>>
): ChecklistResponseEntry => {
  if (isResponseEntry(value)) {
    return value;
  }

  const quantity = extractQuantity(checklistId, fieldName, value, quantities);
  return quantity !== undefined
    ? { value: value as ChecklistScalarValue, quantity }
    : { value: value as ChecklistScalarValue };
};

/**
 * Serialize checklist responses for API submission
 */
export const serializeChecklistResponses = (
  checklistId: number,
  responses: Record<string, ChecklistResponseValue>,
  quantities?: Record<number, Record<string, number>>
): Record<string, ChecklistResponseEntry> => {
  const serialized: Record<string, ChecklistResponseEntry> = {};

  Object.entries(responses).forEach(([fieldName, responseValue]) => {
    const entry = buildResponseEntry(checklistId, fieldName, responseValue, quantities);
    const scalar = entry.value;

    const hasMeaningfulValue = (() => {
      if (Array.isArray(scalar)) {
        return scalar.length > 0;
      }

      if (typeof scalar === "string") {
        return scalar.trim() !== "";
      }

      return scalar !== undefined && scalar !== null;
    })();

    if (!hasMeaningfulValue) {
      return;
    }

    serialized[fieldName] = entry.quantity !== undefined ? entry : { value: scalar };
  });

  return serialized;
};

/**
 * Flatten nested checklist components for pricing calculation
 */
export const flattenComponents = (components: ChecklistComponent[]): ChecklistComponent[] => {
  const result: ChecklistComponent[] = [];

  const flatten = (comps: ChecklistComponent[]) => {
    comps.forEach((component) => {
      result.push(component);
      if (component.nested_components && component.nested_components.length > 0) {
        flatten(component.nested_components);
      }
    });
  };

  flatten(components);
  return result;
};

/**
 * Check if a value has meaningful content
 */
export const hasMeaningfulValue = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === "string") {
    return value.trim() !== "";
  }

  return value !== undefined && value !== null;
};

/**
 * Check if a component requires quantity input
 */
export const componentRequiresQuantity = (component: ChecklistComponent): boolean => {
  if (component.allow_quantity_input) {
    return true;
  }

  const pricingType = component.pricing_type || "fixed";
  return ["per_unit", "per_sqft", "per_linear"].includes(pricingType);
};

/**
 * Clamp quantity value within component's min/max bounds
 */
export const clampQuantity = (component: ChecklistComponent, quantity: number): number => {
  let result = quantity;
  if (component.min_quantity !== undefined && component.min_quantity !== null) {
    result = Math.max(result, component.min_quantity);
  }
  if (component.max_quantity !== undefined && component.max_quantity !== null) {
    result = Math.min(result, component.max_quantity);
  }
  if (Number.isNaN(result) || !Number.isFinite(result)) {
    return 0;
  }
  return result;
};

/**
 * Resolve option value from component option
 */
export const resolveOptionValue = (option: {
  option_value?: string;
  option_label: string;
}): string => {
  if (option.option_value && option.option_value !== "") {
    return option.option_value;
  }
  return option.option_label
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
};
